// src/firebase.js

import { initializeApp } from 'firebase/app';

import {
    getAuth,
    setPersistence,
    browserLocalPersistence
} from 'firebase/auth';

import { getDatabase } from 'firebase/database';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

import firebaseConfig from './utils/firebaseConfig';

const app = initializeApp(firebaseConfig);

/*
|--------------------------------------------------------------------------
| Core Firebase Services
|--------------------------------------------------------------------------
*/

export const auth = getAuth(app);
export const database = getDatabase(app);
export const firestore = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

/*
|--------------------------------------------------------------------------
| Auth Persistence
|--------------------------------------------------------------------------
*/

setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.error('Auth persistence error:', error);
});

/*
|--------------------------------------------------------------------------
| Messaging Support Detection
|--------------------------------------------------------------------------
*/

const isBrowser = typeof window !== 'undefined';

const isSupportedBrowser =
    isBrowser &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

const userAgent =
    isBrowser
        ? navigator.userAgent || navigator.vendor || window.opera
        : '';

const isInAppBrowser =
    /FBAN|FBAV|Instagram|Line|Twitter/i.test(userAgent);

/*
|--------------------------------------------------------------------------
| Lazy Firebase Messaging
|--------------------------------------------------------------------------
*/

let messagingInstance = null;
let messagingModule = null;
let messagingLoadingPromise = null;

export const isMessagingSupported = () => {
    return isSupportedBrowser && !isInAppBrowser;
};

export const getMessagingInstance = async () => {
    if (!isMessagingSupported()) {
        console.warn(
            'Firebase Messaging disabled: unsupported browser or in-app browser.'
        );
        return null;
    }

    if (messagingInstance) {
        return messagingInstance;
    }

    if (messagingLoadingPromise) {
        return messagingLoadingPromise;
    }

    messagingLoadingPromise = (async () => {
        try {
            messagingModule = await import('firebase/messaging');

            messagingInstance = messagingModule.getMessaging(app);

            console.log('Firebase Messaging initialized');

            return messagingInstance;
        } catch (error) {
            console.error('Failed to initialize Firebase Messaging:', error);

            messagingInstance = null;

            return null;
        }
    })();

    return messagingLoadingPromise;
};

/*
|--------------------------------------------------------------------------
| Safe Messaging Helpers
|--------------------------------------------------------------------------
*/

export const getToken = async (options = {}) => {
    try {
        const messaging = await getMessagingInstance();

        if (!messaging || !messagingModule?.getToken) {
            return null;
        }

        return await messagingModule.getToken(messaging, {
            vapidKey: options.vapidKey,
            serviceWorkerRegistration: options.serviceWorkerRegistration
        });
    } catch (error) {
        console.error('Error getting FCM token:', error);
        return null;
    }
};

export const onMessage = async (callback) => {
    try {
        const messaging = await getMessagingInstance();

        if (!messaging || !messagingModule?.onMessage) {
            return () => { };
        }

        return messagingModule.onMessage(
            messaging,
            callback
        );
    } catch (error) {
        console.error('Error attaching onMessage listener:', error);

        return () => { };
    }
};

export const onTokenRefresh = async (callback) => {
    try {
        const messaging = await getMessagingInstance();

        if (!messaging || !messagingModule?.onTokenRefresh) {
            return () => { };
        }

        return messagingModule.onTokenRefresh(
            messaging,
            callback
        );
    } catch (error) {
        console.error('Error attaching onTokenRefresh listener:', error);

        return () => { };
    }
};

/*
|--------------------------------------------------------------------------
| Export App
|--------------------------------------------------------------------------
*/

export { app };

/*
|--------------------------------------------------------------------------
| IMPORTANT
|--------------------------------------------------------------------------
|
| DO NOT export `messaging`.
| Messaging must stay lazy-loaded only.
|
*/